import { GradeAnswerDocument } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { type AIGradingMode, AIGradingModeEnum } from "@ai-teach/types";
import { Button, Modal } from "@ai-teach/ui-web";
import { useMutation } from "@apollo/client/react";
import { useRef, useState } from "react";

/** One read-only info row shown above the answer input (label varies per slide type). */
export interface GradingTestField {
  label: string;
  value: string | null | undefined;
}

interface GradingTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Info rows to display — e.g. Question/Expected Answer, or Word/Sentence. */
  fields: GradingTestField[];
  /** Sent to the grader as the reference answer. */
  correctAnswer: string;
  /** Sent to the grader as the prompt/question context. */
  question: string;
  mode?: AIGradingMode;
  title?: string;
  /** withToast dedupe key, so two modals on one page don't share a toast. */
  toastId?: string;
}

const CARD_STYLE = { borderColor: "#f99508", backgroundColor: "#FFFEF7" } as const;
const LABEL_CLASS = "text-xs font-bold tracking-widest uppercase";
const VALUE_CLASS = "text-base font-extrabold tracking-wide";

export function GradingTestModal({
  isOpen,
  onClose,
  fields,
  correctAnswer,
  question,
  mode = AIGradingModeEnum.Values.strictMode,
  title = "Grading Test",
  toastId = "gradingToast",
}: GradingTestModalProps) {
  const [gradingScore, setGradingScore] = useState<number | null>(null);
  const answerRef = useRef<HTMLInputElement | null>(null);
  const [gradeAnswer, { loading }] = useMutation(GradeAnswerDocument);

  const close = () => {
    setGradingScore(null);
    onClose();
  };

  const handleSubmit = async () => {
    const answer = answerRef.current?.value ?? "";
    if (answer === "") return;
    setGradingScore(null);

    withToast(
      async () => {
        const res = await gradeAnswer({
          variables: { correctAnswer, studentAnswer: answer, question, mode },
        });
        setGradingScore(res.data?.gradeAnswer.score ?? null);
        if (answerRef.current) answerRef.current.value = "";
      },
      {
        error: "Failed to grade answer!",
        success: "Score received!",
        loading: "Grading your answer...",
      },
      toastId
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title={title}>
      <div className="flex flex-col gap-5">
        {fields.map((field) => (
          <div
            key={field.label}
            className="rounded-2xl border-2 px-4 py-3 flex flex-col gap-1"
            style={CARD_STYLE}
          >
            <span className={LABEL_CLASS} style={{ color: "#FF8500" }}>
              {field.label}
            </span>
            <p className={VALUE_CLASS} style={{ color: "#441ce4" }}>
              {field.value || "—"}
            </p>
          </div>
        ))}

        <div className="flex flex-col gap-2">
          <label htmlFor="grading-answer" className={LABEL_CLASS} style={{ color: "#FF8500" }}>
            Your Answer
          </label>
          <input
            id="grading-answer"
            type="text"
            ref={answerRef}
            className="w-full rounded-xl px-4 py-3 text-sm font-semibold outline-none"
            style={{ border: "2px solid #f99508", backgroundColor: "#FFFEF7", color: "#441ce4" }}
            placeholder="Type your answer here..."
          />
        </div>

        <div className="rounded-2xl border-2 px-4 py-3 flex flex-col gap-1" style={CARD_STYLE}>
          <span className={LABEL_CLASS} style={{ color: "#FF8500" }}>
            Score
          </span>
          <p className={VALUE_CLASS} style={{ color: "#441ce4" }}>
            {gradingScore !== null ? gradingScore : "—"}
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <Button
          disabled={loading}
          variant="ghost"
          className="rounded-full w-[130px]"
          onClick={close}
        >
          Cancel
        </Button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="px-6 py-2 rounded-full text-white font-bold text-sm tracking-widest uppercase hover:opacity-90 transition-opacity w-[130px] disabled:opacity-70"
          style={{
            background: "linear-gradient(to right, #FF8500, #FFB800)",
            boxShadow: "0 4px 12px rgba(255, 133, 0, 0.35)",
          }}
        >
          {loading ? "Grading…" : "Submit"}
        </button>
      </div>
    </Modal>
  );
}
